<script setup>
import { computed, defineProps } from 'vue';
import CustomTable from '@/components/table/CustomTable.vue';
import GradientScoreBar from '@/components/GradientScoreBar.vue';

const props = defineProps({
    modelData: {
        type: Array,
        required: true,
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
});

const columns = [
    {
        label: '',
        key: 'rank',
        cellClass: 'font-normal text-white whitespace-nowrap',
        sortable: true,
        type: 'number',
    },
    {
        label: 'Model Name',
        key: 'llm',
        cellClass: 'font-normal text-white whitespace-nowrap',
        sortable: true,
        type: 'text',
    },
    {
        label: 'Score',
        key: 'average',
        cellClass: 'font-normal text-white whitespace-nowrap',
        sortable: true,
        type: 'number',
    },
    {
        label: 'Issue Date',
        key: 'issue_date',
        cellClass: 'font-normal text-white whitespace-nowrap',
        sortable: true,
        type: 'date',
    },
];

const processedData = computed(() => {
    if (!props.modelData || props.modelData.length === 0) return [];
    
    const sortedData = [...props.modelData].sort((a, b) => {
        const scoreA = a.average || 0;
        const scoreB = b.average || 0;
        return scoreB - scoreA;
    });
    
    return sortedData.map((item, index) => ({
        ...item,
        rank: index + 1
    }));
});

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
};




</script>

<template>
    <div class="flex flex-col gap-4 h-full w-full min-h-[300px]">
        <div v-if="isLoading" class="h-[300px] w-full flex justify-center items-center">
            <div class="animate-spin rounded-full h-12 w-12 border-4 border-echoes-border-gray border-t-echoes-primary"></div>
        </div>
        <CustomTable v-else-if="!isLoading" :data="processedData" :columns="columns" search-placeholder="Search..." :searchable="true">
            <template #cell-llm="{ value, item }">
                <div class="flex items-center gap-2">
                    <div v-if="item?.llm_icon" class="h-5 w-5 rounded-full overflow-hidden bg-[#0F0F0F] flex-shrink-0">
                        <img :src="item.llm_icon" :alt="value ? `${value} logo` : 'Model logo'" class="h-full w-full object-cover" />
                    </div>
                    <i v-else class="mdi mdi-robot text-white/60 text-sm"></i>
                    <span class="text-white/90">{{ value }}</span>
                </div>
            </template>
            <template #cell-average="{ value }">
                <div class="flex items-center gap-3">
                    <GradientScoreBar 
                        :value="value / 100" 
                        orientation="horizontal"
                        length="w-32"
                        thickness="h-5"
                        rounded-size="rounded-full"
                        background="bg-[#0F0F0F]"
                    />
                    <span class="text-white font-semibold text-sm whitespace-nowrap transition-all duration-300">
                        {{ Math.round(value / 100) }}%
                    </span>
                </div>
            </template>
            <template #cell-issue_date="{ value }">
                <span class="text-white/80">
                    {{ formatDate(value) }}
                </span>
            </template>
        </CustomTable>
    </div>
</template>